import { useEffect } from "react";

import { JiraSoftwarePricingPageProps } from "src/pages/software/jira/pricing/jira-software-pricing-page";

import { PricingFeatureTable } from "@/components/wpl/pricing/src/features-table";
import {
    DataCenterSection,
    QuestionsCta,
    AtlassianGuardAddOnShared
} from "@/components/wpl/pricing/src/index.ts";
import {
    usePricingContentContext,
    useDatacenterPlans
} from "@/components/wpl/pricing/src/pricing-content-context.tsx";
import { QuotationsAdornment } from "@/components/wpl/pricing/src/pricing-faq";
import { PricingFaq } from "@/components/wpl/pricing/src/pricing-faq/pricing-faq";
import {
    PageHeading,
    PricingPageLayout
} from "@/components/wpl/pricing/src/pricing-page-layout";
import { useLocalizedHref } from "@/libraries/web-framework";
import {
    experiments,
    ExperimentView,
    useExperimentExposure
} from "@/utils/experiment";
import { CallToAction } from "@/utils/pricing-queries/get-pricing-content-gql.ts";
import { ParentScrollTracking } from "@/utils/scroll-tracking/parent-scroll-tracker.tsx";

import JiraSoftwareNavV2 from "../../sections/nav-bar/software-jira-navbar";

import { JiraPriceFinder } from "./sections/jira-price-finder";
import {
    JiraPriceFinderJfind1384,
    JiraPriceFinderJfind1384Props
} from "./sections/jira-price-finder-jfind-1384";
import { Jfind1384PageHeading } from "./variants/jfind-1384-page-heading";

// eslint-disable-next-line complexity
export const JiraSoftwarePricingPageV2Template = (
    props: JiraSoftwarePricingPageProps
) => {
    const content = usePricingContentContext();
    const questionsCtaContent = content
        ?.callToActionContainer?.[0] as CallToAction;

    const featuresCtaContent = content
        ?.callToActionContainer?.[1] as CallToAction;
    const dataCenterContent = useDatacenterPlans("Contact Us", "Learn more");

    const [exposureJiraPricingFired, fireJiraPricingExposure] =
        useExperimentExposure(experiments.jfind1384OptimizeJiraPricing);

    useEffect(() => {
        if (!exposureJiraPricingFired) {
            fireJiraPricingExposure();
        }
    }, [exposureJiraPricingFired, fireJiraPricingExposure]);

    return (
        <PricingPageLayout
            nav={<JiraSoftwareNavV2 />}
            showFallback={content?.hasError}
        >
            <ParentScrollTracking
                scrollTrackingEnabled={true}
                pageId="jira-software-pricing-page"
            >
                <ExperimentView
                    experiment={experiments.jfind1384OptimizeJiraPricing}
                    render={(value) =>
                        value ? (
                            <>
                                <Jfind1384PageHeading />
                                <JiraPriceFinderJfind1384
                                    ssrCcpPricingData={
                                        props.pricing?.ccpPricingData
                                            ?.jiraSoftware as JiraPriceFinderJfind1384Props["ssrCcpPricingData"]
                                    }
                                    countryCode={props.countryCode}
                                />
                            </>
                        ) : (
                            <>
                                <PageHeading>{content?.headline}</PageHeading>
                                <JiraPriceFinder
                                    pricing={props.pricing}
                                    countryCode={props.countryCode}
                                />
                            </>
                        )
                    }
                />
                <PricingFeatureTable
                    title={content?.compareFeaturesTitle ?? undefined}
                    comparePlans={content?.comparePlans ?? undefined}
                    compareFeatures={content?.compareFeatures ?? undefined}
                    linkValues={{
                        linkText: featuresCtaContent?.text ?? undefined,
                        linkHref: featuresCtaContent?.url ?? undefined
                    }}
                    dataProductKey={"jiraSoftware"}
                    stickyCtasEnabled={false}
                />
                <AtlassianGuardAddOnShared
                    ctaHref={useLocalizedHref("/software/guard")}
                />
                <DataCenterSection {...dataCenterContent} />
                <QuotationsAdornment>
                    <ParentScrollTracking>
                        <PricingFaq
                            header={content?.getMoreDetailsTitle ?? undefined}
                            planDetails={
                                content?.additionalDetails ?? undefined
                            }
                        />
                    </ParentScrollTracking>
                </QuotationsAdornment>
                <QuestionsCta
                    product={"Jira"}
                    ctaHref={questionsCtaContent?.url ?? undefined}
                    questionsHeader={questionsCtaContent?.title ?? undefined}
                    priceAndLicensingLinkText={
                        questionsCtaContent?.text ?? undefined
                    }
                    questionsBody={
                        questionsCtaContent?.valueProposition ?? undefined
                    }
                    imageHref={questionsCtaContent?.asset?.url}
                />
            </ParentScrollTracking>
        </PricingPageLayout>
    );
};
